import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, catchError, debounceTime, map, of, switchMap, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MetaDataService } from '../../../services/meta/meta-data.service';
import {
  UgcCreateDraftLocationSnapshot,
  UgcCreateDraftService
} from '../../../services/ugc-create-draft.service';
import { UgcService } from '../../../services/ugc.service';
import { TerritoryService } from '../../../services/geolocation/territory.service';
import { UserGeolocationService } from '../../../services/geolocation/user-geolocation.service';
import { ToastService } from '../../../services/toast/toast.service';
import { ImageManager } from '../../../shared/image-manager';
import {
  GeoPositionDTO,
  SimpleXploreItemDTO,
  UgcCreateMediaSubmissionRequest,
  UgcCreateXploreSubmissionRequest,
  UgcEligibilityResponse,
  UgcNameCheckResponse,
  UgcPhotoUploadDTO,
  UgcProgramStatus,
  UgcSubmissionDTO
} from '../../../shared/object/xplorer-be-parsed-classes';

type UgcCreateFlowStep = 'name' | 'check' | 'camera' | 'preview' | 'success';

type UgcTargetSelection =
  | { mode: 'new' }
  | { mode: 'existing'; item: SimpleXploreItemDTO };

type UgcLocationState = 'idle' | 'loading' | 'granted' | 'denied' | 'timeout' | 'unavailable' | 'error';

type UgcSubmitState = 'idle' | 'validating' | 'uploading' | 'success' | 'error';

type UgcNameCheckResult =
  | { status: 'idle' }
  | { status: 'success'; response: UgcNameCheckResponse }
  | { status: 'error'; message: string };

@Component({
  selector: 'xplorer-bowser-ugc-create-page',
  templateUrl: './ugc-create-page.component.html',
  styleUrl: './ugc-create-page.component.css',
  imports: [RouterLink, TranslateModule]
})
export class UgcCreatePageComponent {
  private static readonly MIN_NAME_CHECK_LENGTH = 3;
  private static readonly MIN_PHOTO_LONGEST_EDGE = 640;
  private static readonly MIN_PHOTO_SHORTEST_EDGE = 480;

  @ViewChild('cameraVideo') private cameraVideo?: ElementRef<HTMLVideoElement>;
  @ViewChild('cameraInput') private cameraInput?: ElementRef<HTMLInputElement>;

  readonly isLoadingEligibility = signal(true);
  readonly eligibility = signal<UgcEligibilityResponse | null>(null);
  readonly eligibilityError = signal(false);
  readonly territoryCode = signal('');

  readonly flowStep = signal<UgcCreateFlowStep>('name');
  readonly xploreName = signal('');
  readonly nameError = signal(false);
  readonly nameTooShort = signal(false);
  readonly isCheckingName = signal(false);
  readonly nameCheckResponse = signal<UgcNameCheckResponse | null>(null);
  readonly nameCheckError = signal<string | null>(null);
  readonly selectedTarget = signal<UgcTargetSelection | null>(null);

  readonly isStartingCamera = signal(false);
  readonly cameraError = signal<string | null>(null);
  readonly usingFileFallback = signal(false);
  readonly capturedPhoto = signal<UgcPhotoUploadDTO | null>(null);
  readonly previewDataUrl = signal<string | null>(null);

  readonly isResolvingLocation = signal(false);
  readonly locationState = signal<UgcLocationState>('idle');
  readonly currentLocation = signal<GeolocationPosition | null>(null);

  readonly submitState = signal<UgcSubmitState>('idle');
  readonly isSubmitting = computed(() => {
    const state = this.submitState();
    return state === 'validating' || state === 'uploading';
  });
  readonly submissionError = signal<string | null>(null);
  readonly submittedSubmission = signal<UgcSubmissionDTO | null>(null);

  readonly programStatus = computed<UgcProgramStatus | null>(() => this.eligibility()?.programStatus ?? null);
  readonly hasAcceptedProgram = computed(() => this.programStatus() === 'ACCEPTED');
  readonly canContribute = computed(() => this.hasAcceptedProgram() && this.eligibility()?.allowed === true);
  readonly canCreateNew = computed(() => this.nameCheckResponse()?.canCreateNew === true);
  readonly similarMatches = computed(() => this.nameCheckResponse()?.similarMatches ?? []);
  readonly hasSimilarMatches = computed(() => this.similarMatches().length > 0);
  readonly hasExactMatch = computed(() => this.nameCheckResponse()?.exactMatch === true);
  readonly showStandaloneCreateNew = computed(() => this.canCreateNew() && !this.hasSimilarMatches());
  readonly showInlineCreateNew = computed(() => this.canCreateNew() && this.hasSimilarMatches());
  readonly showBlockedNoResults = computed(() => !this.canCreateNew() && !this.hasSimilarMatches());
  readonly selectedExistingItem = computed(() => {
    const selectedTarget = this.selectedTarget();
    return selectedTarget?.mode === 'existing' ? selectedTarget.item : null;
  });
  readonly isNewTarget = computed(() => this.selectedTarget()?.mode === 'new');
  readonly stageIndex = computed(() => {
    const step = this.flowStep();

    if (step === 'name' || step === 'check') {
      return 1;
    }

    if (step === 'camera' || step === 'preview') {
      return 2;
    }

    return 3;
  });
  readonly showLocationSupportCard = computed(() => this.isNewTarget() && this.locationState() !== 'granted');
  readonly showSubmitProgressCard = computed(() => {
    const state = this.submitState();
    return state === 'validating' || state === 'uploading';
  });
  readonly eligibilityBlockedReasonKeys = computed(() => {
    const eligibility = this.eligibility();
    if (!eligibility || this.canContribute() || !this.hasAcceptedProgram()) {
      return [];
    }

    return this.resolveEligibilityBlockedReasonKeys(eligibility, 'ugc_create.eligibility.reasons');
  });
  readonly canSubmit = computed(() => {
    if (!this.capturedPhoto() || this.isSubmitting()) {
      return false;
    }

    if (this.isNewTarget()) {
      return this.locationState() === 'granted' && !!this.currentLocation();
    }

    return true;
  });

  private readonly ugcService = inject(UgcService);
  private readonly ugcCreateDraftService = inject(UgcCreateDraftService);
  private readonly territoryService = inject(TerritoryService);
  private readonly metaDataService = inject(MetaDataService);
  private readonly userGeolocationService = inject(UserGeolocationService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly nameCheckInput$ = new Subject<string>();
  private cameraStream: MediaStream | null = null;
  private isDraftPersistenceReady = false;
  private isRestoringDraft = false;

  constructor() {
    this.territoryCode.set(this.territoryService.getTerritoryCodeFromUrl());
    this.metaDataService.setDefaultMetaData();
    this.destroyRef.onDestroy(() => this.stopCameraStream());
    this.initDraftPersistence();
    this.initAutoNameCheck();
    this.loadEligibility();
  }

  submitName(): void {
    const normalizedName = this.xploreName().trim();
    if (!normalizedName || !this.canContribute()) {
      this.nameError.set(!normalizedName);
      this.nameTooShort.set(false);
      return;
    }

    if (normalizedName.length < UgcCreatePageComponent.MIN_NAME_CHECK_LENGTH) {
      this.nameError.set(false);
      this.nameTooShort.set(true);
      return;
    }

    this.nameTooShort.set(false);
    this.runNameCheck(normalizedName);
  }

  onNameInput(value: string): void {
    this.xploreName.set(value);
    this.nameError.set(false);
    this.nameTooShort.set(false);
    this.nameCheckError.set(null);
    this.selectedTarget.set(null);
    this.nameCheckResponse.set(null);
    this.flowStep.set('name');

    const normalizedName = value.trim();
    if (!normalizedName || normalizedName.length < UgcCreatePageComponent.MIN_NAME_CHECK_LENGTH || !this.canContribute()) {
      this.isCheckingName.set(false);
      return;
    }

    this.isCheckingName.set(true);
    this.nameCheckInput$.next(normalizedName);
  }

  chooseCreateNew(): void {
    this.selectedTarget.set({ mode: 'new' });
    this.openCameraStep();
  }

  chooseExisting(item: SimpleXploreItemDTO): void {
    this.selectedTarget.set({ mode: 'existing', item });
    this.openCameraStep();
  }

  startFallbackCamera(): void {
    this.cameraInput?.nativeElement?.click();
  }

  captureFromLiveCamera(): void {
    const videoElement = this.cameraVideo?.nativeElement;
    if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
      this.cameraError.set(this.translateService.instant('ugc_create.camera.unavailable'));
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      this.cameraError.set(this.translateService.instant('ugc_create.camera.unavailable'));
      return;
    }

    context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const base64data = dataUrl.split(',')[1] ?? '';
    const photo: UgcPhotoUploadDTO = {
      base64data,
      fileExtension: 'jpg',
      contentType: 'image/jpeg',
      originalFilename: `ugc-${Date.now()}.jpg`
    };

    if (!this.applyCapturedPhoto(photo, dataUrl, canvas.width, canvas.height)) {
      return;
    }

    this.stopCameraStream();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    this.readFileAsPhotoUpload(file)
      .then(({ photo, previewDataUrl, width, height }) => {
        this.applyCapturedPhoto(photo, previewDataUrl, width, height);
      })
      .catch(() => {
        this.cameraError.set(this.translateService.instant('ugc_create.camera.file_error'));
      })
      .finally(() => {
        if (input) {
          input.value = '';
        }
      });
  }

  retakePhoto(): void {
    this.capturedPhoto.set(null);
    this.previewDataUrl.set(null);
    this.submitState.set('idle');
    this.submissionError.set(null);
    this.openCameraStep();
  }

  submitContribution(): void {
    const photo = this.capturedPhoto();
    if (!photo) {
      this.cameraError.set(this.translateService.instant('ugc_create.camera.photo_required'));
      return;
    }

    if (!this.canSubmit()) {
      return;
    }

    const selection = this.selectedTarget();
    if (!photo || !selection) {
      return;
    }

    this.submitState.set('validating');
    this.submissionError.set(null);

    if (selection.mode === 'new') {
      const position = this.currentLocation();
      if (!position?.coords) {
        this.submitState.set('error');
        this.locationState.set(this.locationState() === 'loading' ? 'loading' : 'unavailable');
        this.submissionError.set(this.translateService.instant('ugc_create.submit.location_required'));
        return;
      }

      const request: UgcCreateXploreSubmissionRequest = {
        name: this.xploreName().trim(),
        territoryCode: this.territoryCode(),
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        shortDescription: '',
        capturedAt: new Date(),
        photo
      };

      this.submitState.set('uploading');
      this.ugcService.submitNewXplore(request)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (submission) => this.handleSubmitSuccess(submission),
          error: (error) => this.handleSubmitError(error)
        });
      return;
    }

    const request: Partial<UgcCreateMediaSubmissionRequest> = {
      capturedAt: new Date(),
      photo
    };

    const position = this.currentLocation();
    if (position?.coords) {
      request.latitude = position.coords.latitude;
      request.longitude = position.coords.longitude;
    }

    this.submitState.set('uploading');
    this.ugcService.submitExistingXploreMedia(selection.item.id, request as UgcCreateMediaSubmissionRequest)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (submission) => this.handleSubmitSuccess(submission),
        error: (error) => this.handleSubmitError(error)
      });
  }

  restartFlow(): void {
    this.stopCameraStream();
    this.ugcCreateDraftService.clearDraft(this.territoryCode());
    this.flowStep.set('name');
    this.xploreName.set('');
    this.nameError.set(false);
    this.nameTooShort.set(false);
    this.isCheckingName.set(false);
    this.nameCheckResponse.set(null);
    this.nameCheckError.set(null);
    this.selectedTarget.set(null);
    this.capturedPhoto.set(null);
    this.previewDataUrl.set(null);
    this.currentLocation.set(null);
    this.locationState.set('idle');
    this.cameraError.set(null);
    this.usingFileFallback.set(false);
    this.submitState.set('idle');
    this.submissionError.set(null);
    this.submittedSubmission.set(null);
  }

  backToCheckStep(): void {
    this.stopCameraStream();
    this.selectedTarget.set(null);
    this.capturedPhoto.set(null);
    this.previewDataUrl.set(null);
    this.cameraError.set(null);
    this.submitState.set('idle');
    this.submissionError.set(null);
    this.usingFileFallback.set(false);
    this.flowStep.set('check');
  }

  getProgramGateTitleKey(): string {
    const status = this.programStatus();

    if (status === 'INVITED' || status === 'ELIGIBLE') {
      return 'ugc_create.program_gate.ready_title';
    }

    return 'ugc_create.program_gate.waiting_title';
  }

  getProgramGateBodyKey(): string {
    const status = this.programStatus();

    if (status === 'INVITED' || status === 'ELIGIBLE') {
      return 'ugc_create.program_gate.ready_body';
    }

    return 'ugc_create.program_gate.waiting_body';
  }

  getFlowHintKey(): string {
    const step = this.flowStep();

    if (step === 'name' || step === 'check') {
      return 'ugc_create.context.search';
    }

    if (step === 'camera') {
      return this.isNewTarget() ? 'ugc_create.context.camera_new' : 'ugc_create.context.camera_existing';
    }

    if (step === 'preview') {
      return this.isNewTarget() ? 'ugc_create.context.preview_new' : 'ugc_create.context.preview_existing';
    }

    return 'ugc_create.context.success';
  }

  getStageTitleKey(): string {
    const step = this.flowStep();

    if (step === 'name' || step === 'check') {
      return 'ugc_create.stage_header.search_title';
    }

    if (step === 'camera') {
      return this.isNewTarget()
        ? 'ugc_create.stage_header.camera_new_title'
        : 'ugc_create.stage_header.camera_existing_title';
    }

    if (step === 'preview') {
      return this.isNewTarget()
        ? 'ugc_create.stage_header.preview_new_title'
        : 'ugc_create.stage_header.preview_existing_title';
    }

    return 'ugc_create.stage_header.success_title';
  }

  getLocationChipKey(): string {
    switch (this.locationState()) {
      case 'granted':
        return 'ugc_create.location.granted';
      case 'denied':
        return 'ugc_create.location.denied';
      case 'timeout':
        return 'ugc_create.location.timeout';
      case 'unavailable':
      case 'error':
        return 'ugc_create.location.unavailable';
      default:
        return 'ugc_create.location.loading';
    }
  }

  getLocationSupportTitleKey(): string {
    switch (this.locationState()) {
      case 'loading':
        return 'ugc_create.location.loading_title';
      case 'denied':
        return 'ugc_create.location.denied_title';
      case 'timeout':
        return 'ugc_create.location.timeout_title';
      case 'unavailable':
      case 'error':
        return 'ugc_create.location.retry_title';
      default:
        return 'ugc_create.location.required_title';
    }
  }

  getLocationSupportBodyKey(): string {
    switch (this.locationState()) {
      case 'loading':
        return 'ugc_create.location.loading_body';
      case 'denied':
        return 'ugc_create.location.denied_body';
      case 'timeout':
        return 'ugc_create.location.timeout_body';
      case 'unavailable':
      case 'error':
        return 'ugc_create.location.retry_body';
      default:
        return 'ugc_create.location.required_body';
    }
  }

  shouldShowLocationRetry(): boolean {
    const state = this.locationState();
    return state === 'denied' || state === 'timeout' || state === 'unavailable' || state === 'error';
  }

  getSubmitPrimaryLabelKey(): string {
    switch (this.submitState()) {
      case 'validating':
        return 'ugc_create.submit.validating';
      case 'uploading':
        return 'ugc_create.submit.uploading';
      default:
        return 'ugc_create.submit.cta';
    }
  }

  getSubmitProgressTitleKey(): string {
    return this.submitState() === 'validating'
      ? 'ugc_create.submit.validating_title'
      : 'ugc_create.submit.uploading_title';
  }

  getSubmitProgressBodyKey(): string {
    return this.submitState() === 'validating'
      ? 'ugc_create.submit.validating_body'
      : 'ugc_create.submit.uploading_body';
  }

  getCameraPanelTitleKey(): string {
    if (this.flowStep() === 'preview') {
      return this.isNewTarget()
        ? 'ugc_create.camera.preview_new_title'
        : 'ugc_create.camera.preview_existing_title';
    }

    return this.isNewTarget()
      ? 'ugc_create.camera.stage_new_title'
      : 'ugc_create.camera.stage_existing_title';
  }

  getCameraPanelBodyKey(): string {
    if (this.flowStep() === 'preview') {
      return this.isNewTarget()
        ? 'ugc_create.camera.preview_new_body'
        : 'ugc_create.camera.preview_existing_body';
    }

    return this.isNewTarget()
      ? 'ugc_create.camera.stage_new_body'
      : 'ugc_create.camera.stage_existing_body';
  }

  retryLocation(): void {
    if (this.isResolvingLocation()) {
      return;
    }

    this.resolveLocation();
  }

  getMatchImageUrl(match: SimpleXploreItemDTO): string {
    const imageUrl = match.multiMediaUrls?.[0];
    return imageUrl ? `${environment.IMAGES_URL}${imageUrl}` : ImageManager.getPlaceholderImage();
  }

  getMatchLocationLabel(match: SimpleXploreItemDTO): string {
    return match.geoPosition?.location?.name
      || match.geoPosition?.location?.comune?.name
      || match.geoPosition?.comune?.name
      || this.translateService.instant('home.location_unavailable');
  }

  getMatchDistanceLabel(match: SimpleXploreItemDTO): string | null {
    const position = this.currentLocation();
    const geoPosition = match.geoPosition;
    if (!position?.coords || !geoPosition) {
      return null;
    }

    const distance = this.calculateDistanceInKm(position, geoPosition);
    if (distance === null) {
      return null;
    }

    const formattedDistance = distance < 10 ? distance.toFixed(1) : Math.round(distance).toString();
    return this.translateService.instant('ugc_create.matches.distance', {
      distance: formattedDistance
    });
  }

  getResultsTitleKey(): string {
    return this.hasExactMatch()
      ? 'ugc_create.matches.exact_title'
      : 'ugc_create.matches.similar_title';
  }

  getResultsBodyKey(): string {
    return this.hasExactMatch()
      ? 'ugc_create.matches.exact_body'
      : 'ugc_create.matches.similar_body';
  }

  private initAutoNameCheck(): void {
    this.nameCheckInput$
      .pipe(
        map((name) => name.trim()),
        debounceTime(550),
        switchMap((name) => {
          if (!name || !this.canContribute()) {
            return of<UgcNameCheckResult>({ status: 'idle' });
          }

          return this.ugcService.nameCheck(this.territoryCode(), name).pipe(
            map((response) => ({ status: 'success', response }) as UgcNameCheckResult),
            catchError((error) => of<UgcNameCheckResult>({
              status: 'error',
              message: this.resolveHttpError(error, 'ugc_create.name_step.error')
            }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        this.isCheckingName.set(false);

        if (result.status === 'idle') {
          return;
        }

        if (result.status === 'error') {
          this.nameCheckError.set(result.message);
          return;
        }

        this.handleNameCheckSuccess(result.response);
      });
  }

  private initDraftPersistence(): void {
    effect(() => {
      const territoryCode = this.territoryCode();
      const canContribute = this.canContribute();
      const flowStep = this.flowStep();
      const xploreName = this.xploreName();
      const nameCheckResponse = this.nameCheckResponse();
      const selectedTarget = this.selectedTarget();
      const capturedPhoto = this.capturedPhoto();
      const currentLocation = this.currentLocation();

      if (!this.isDraftPersistenceReady || this.isRestoringDraft) {
        return;
      }

      if (!territoryCode || !canContribute || flowStep === 'success') {
        this.ugcCreateDraftService.clearDraft(territoryCode);
        return;
      }

      const hasContent = !!xploreName.trim() || !!nameCheckResponse || !!selectedTarget || !!capturedPhoto;
      if (!hasContent) {
        this.ugcCreateDraftService.clearDraft(territoryCode);
        return;
      }

      this.ugcCreateDraftService.saveDraft(territoryCode, {
        xploreName,
        nameCheckResponse,
        selectedTarget,
        capturedPhoto,
        currentLocation: this.toDraftLocationSnapshot(currentLocation)
      });
    });
  }

  private runNameCheck(name: string): void {
    this.isCheckingName.set(true);
    this.nameCheckError.set(null);
    this.selectedTarget.set(null);
    this.nameCheckResponse.set(null);

    this.ugcService.nameCheck(this.territoryCode(), name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.handleNameCheckSuccess(response);
          this.isCheckingName.set(false);
        },
        error: (error) => {
          this.nameCheckError.set(this.resolveHttpError(error, 'ugc_create.name_step.error'));
          this.isCheckingName.set(false);
        }
      });
  }

  private loadEligibility(): void {
    this.ugcService.getEligibility()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (eligibility) => {
          this.eligibility.set(eligibility);
          this.isLoadingEligibility.set(false);
          this.restoreDraftIfAvailable();
          this.isDraftPersistenceReady = true;
        },
        error: () => {
          this.eligibilityError.set(true);
          this.isLoadingEligibility.set(false);
          this.ugcCreateDraftService.clearDraft(this.territoryCode());
          this.isDraftPersistenceReady = true;
        }
      });
  }

  private handleNameCheckSuccess(response: UgcNameCheckResponse): void {
    this.nameCheckResponse.set(response);
    this.flowStep.set('check');
    this.ensureLocationForMatchDistances(response);
  }

  private ensureLocationForMatchDistances(response: UgcNameCheckResponse): void {
    if (this.currentLocation() || this.isResolvingLocation()) {
      return;
    }

    const hasGeolocatedMatches = response.similarMatches?.some(
      (match) => match.geoPosition?.latitude != null && match.geoPosition?.longitude != null
    );

    if (!hasGeolocatedMatches) {
      return;
    }

    this.resolveLocation();
  }

  private openCameraStep(): void {
    this.stopCameraStream();
    this.cameraError.set(null);
    this.capturedPhoto.set(null);
    this.previewDataUrl.set(null);
    this.usingFileFallback.set(false);
    this.flowStep.set('camera');
    if (this.currentLocation()) {
      this.locationState.set('granted');
    } else if (this.isResolvingLocation()) {
      this.locationState.set('loading');
    } else {
      this.resolveLocation();
    }

    if (!this.supportsLiveCamera()) {
      this.usingFileFallback.set(true);
      return;
    }

    this.isStartingCamera.set(true);
    navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      },
      audio: false
    }).then((stream) => {
      this.cameraStream = stream;
      this.isStartingCamera.set(false);
      setTimeout(() => this.attachStreamToVideo(stream), 0);
    }).catch(() => {
      this.isStartingCamera.set(false);
      this.usingFileFallback.set(true);
      this.cameraError.set(this.translateService.instant('ugc_create.camera.permission_denied'));
    });
  }

  private resolveLocation(): void {
    this.isResolvingLocation.set(true);
    this.locationState.set('loading');
    this.currentLocation.set(null);

    this.userGeolocationService.getCurrentUserLocation()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (position) => {
          if (position?.coords) {
            this.currentLocation.set(position);
            this.locationState.set('granted');
          } else {
            this.locationState.set('unavailable');
          }
          this.isResolvingLocation.set(false);
        },
        error: (error) => {
          this.isResolvingLocation.set(false);
          this.locationState.set(this.resolveLocationErrorState(error));
        }
      });
  }

  private supportsLiveCamera(): boolean {
    return this.isBrowser && !!navigator?.mediaDevices?.getUserMedia;
  }

  private attachStreamToVideo(stream: MediaStream): void {
    const videoElement = this.cameraVideo?.nativeElement;
    if (!videoElement) {
      return;
    }

    videoElement.srcObject = stream;
    void videoElement.play().catch(() => {
      this.cameraError.set(this.translateService.instant('ugc_create.camera.unavailable'));
    });
  }

  private stopCameraStream(): void {
    if (this.cameraVideo?.nativeElement) {
      this.cameraVideo.nativeElement.pause();
      this.cameraVideo.nativeElement.srcObject = null;
    }

    this.cameraStream?.getTracks().forEach((track) => track.stop());
    this.cameraStream = null;
  }

  private async readFileAsPhotoUpload(file: File): Promise<{
    photo: UgcPhotoUploadDTO;
    previewDataUrl: string;
    width: number;
    height: number;
  }> {
    const previewDataUrl = await this.readFileAsDataUrl(file);
    const dimensions = await this.readImageDimensions(previewDataUrl);
    const base64data = previewDataUrl.split(',')[1] ?? '';
    const fileExtension = this.extractFileExtension(file);

    return {
      previewDataUrl,
      width: dimensions.width,
      height: dimensions.height,
      photo: {
        base64data,
        fileExtension,
        contentType: file.type || 'image/jpeg',
        originalFilename: file.name || `ugc-${Date.now()}.${fileExtension}`
      }
    };
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private readImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve({
        width: image.naturalWidth,
        height: image.naturalHeight
      });
      image.onerror = reject;
      image.src = dataUrl;
    });
  }

  private extractFileExtension(file: File): string {
    const fromName = file.name?.split('.').pop()?.toLowerCase();
    if (fromName) {
      return fromName;
    }

    if (file.type === 'image/png') {
      return 'png';
    }

    if (file.type === 'image/webp') {
      return 'webp';
    }

    return 'jpg';
  }

  private calculateDistanceInKm(position: GeolocationPosition, geoPosition: GeoPositionDTO): number | null {
    if (!position?.coords || geoPosition.latitude == null || geoPosition.longitude == null) {
      return null;
    }

    const lat1 = position.coords.latitude;
    const lon1 = position.coords.longitude;
    const lat2 = geoPosition.latitude;
    const lon2 = geoPosition.longitude;

    const radius = 6371e3;
    const phi1 = lat1 * Math.PI / 180;
    const phi2 = lat2 * Math.PI / 180;
    const deltaPhi = (lat2 - lat1) * Math.PI / 180;
    const deltaLambda = (lon2 - lon1) * Math.PI / 180;

    const haversine = Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) * Math.cos(phi2) *
      Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2);

    const angularDistance = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
    return (radius * angularDistance) / 1000;
  }

  private resolveEligibilityBlockedReasonKeys(eligibility: UgcEligibilityResponse, translationPrefix: string): string[] {
    const reasons: string[] = [];
    const currentReputation = eligibility.currentReputation ?? 0;
    const minimumRequiredReputation = eligibility.minimumRequiredReputation ?? 0;
    const pendingSubmissions = eligibility.pendingSubmissions ?? 0;
    const maxPendingSubmissions = eligibility.maxPendingSubmissions ?? 0;

    if (minimumRequiredReputation > 0 && currentReputation < minimumRequiredReputation) {
      reasons.push(`${translationPrefix}.reputation_too_low`);
    }

    if (maxPendingSubmissions > 0 && pendingSubmissions >= maxPendingSubmissions) {
      reasons.push(`${translationPrefix}.pending_limit_reached`);
    }

    if (!reasons.length && eligibility.allowed === false) {
      reasons.push(`${translationPrefix}.generic_blocked`);
    }

    return reasons;
  }

  private resolveLocationErrorState(error: unknown): UgcLocationState {
    if (this.isLocationPermissionDeniedError(error)) {
      return 'denied';
    }

    if (this.isLocationTimeoutError(error)) {
      return 'timeout';
    }

    if (this.isLocationPositionUnavailableError(error)) {
      return 'unavailable';
    }

    return 'error';
  }

  private isLocationPermissionDeniedError(error: unknown): boolean {
    if (!error) {
      return false;
    }

    const geolocationError = error as GeolocationPositionError;
    if (typeof geolocationError.code === 'number') {
      return geolocationError.code === geolocationError.PERMISSION_DENIED || geolocationError.code === 1;
    }

    const errorMessage = String((error as { message?: unknown })?.message || '').toLowerCase();
    return errorMessage.includes('permission') || errorMessage.includes('denied');
  }

  private isLocationTimeoutError(error: unknown): boolean {
    if (!error) {
      return false;
    }

    const geolocationError = error as GeolocationPositionError;
    if (typeof geolocationError.code === 'number') {
      return geolocationError.code === geolocationError.TIMEOUT || geolocationError.code === 3;
    }

    const errorMessage = String((error as { message?: unknown })?.message || '').toLowerCase();
    return errorMessage.includes('timeout') || errorMessage.includes('timed out');
  }

  private isLocationPositionUnavailableError(error: unknown): boolean {
    if (!error) {
      return false;
    }

    const geolocationError = error as GeolocationPositionError;
    if (typeof geolocationError.code === 'number') {
      return geolocationError.code === geolocationError.POSITION_UNAVAILABLE || geolocationError.code === 2;
    }

    const errorMessage = String((error as { message?: unknown })?.message || '').toLowerCase();
    return errorMessage.includes('position') || errorMessage.includes('unavailable');
  }

  private restoreDraftIfAvailable(): void {
    if (!this.canContribute()) {
      this.ugcCreateDraftService.clearDraft(this.territoryCode());
      return;
    }

    const draft = this.ugcCreateDraftService.getDraft(this.territoryCode());
    if (!draft) {
      return;
    }

    this.isRestoringDraft = true;

    try {
      this.stopCameraStream();
      this.xploreName.set(draft.xploreName ?? '');
      this.nameError.set(false);
      this.nameTooShort.set(false);
      this.isCheckingName.set(false);
      this.nameCheckError.set(null);
      this.cameraError.set(null);
      this.submitState.set('idle');
      this.submissionError.set(null);
      this.submittedSubmission.set(null);
      this.usingFileFallback.set(false);
      this.nameCheckResponse.set(draft.nameCheckResponse ?? null);
      this.selectedTarget.set(draft.selectedTarget ?? null);

      if (draft.currentLocation) {
        this.currentLocation.set(this.fromDraftLocationSnapshot(draft.currentLocation));
        this.locationState.set('granted');
      } else {
        this.currentLocation.set(null);
        this.locationState.set('idle');
      }

      if (draft.capturedPhoto) {
        this.capturedPhoto.set(draft.capturedPhoto);
        this.previewDataUrl.set(this.buildPreviewDataUrl(draft.capturedPhoto));
        this.flowStep.set('preview');

        if (draft.selectedTarget?.mode === 'new' && !draft.currentLocation) {
          this.resolveLocation();
        }
      } else if (draft.selectedTarget) {
        this.capturedPhoto.set(null);
        this.previewDataUrl.set(null);
        this.openCameraStep();
      } else if (draft.nameCheckResponse) {
        this.capturedPhoto.set(null);
        this.previewDataUrl.set(null);
        this.flowStep.set('check');
        this.ensureLocationForMatchDistances(draft.nameCheckResponse);
      } else {
        this.capturedPhoto.set(null);
        this.previewDataUrl.set(null);
        this.flowStep.set('name');
      }
    } finally {
      this.isRestoringDraft = false;
    }

    this.toastService.showInfoToast(this.translateService.instant('ugc_create.draft.restored_toast'));
  }

  private applyCapturedPhoto(
    photo: UgcPhotoUploadDTO,
    previewDataUrl: string,
    width: number,
    height: number
  ): boolean {
    const validationError = this.resolvePhotoValidationError(width, height);
    if (validationError) {
      this.capturedPhoto.set(null);
      this.previewDataUrl.set(null);
      this.submitState.set('idle');
      this.submissionError.set(null);
      this.cameraError.set(validationError);
      return false;
    }

    this.capturedPhoto.set(photo);
    this.previewDataUrl.set(previewDataUrl);
    this.cameraError.set(null);
    this.submitState.set('idle');
    this.submissionError.set(null);
    this.flowStep.set('preview');
    return true;
  }

  private resolvePhotoValidationError(width: number, height: number): string | null {
    const longestEdge = Math.max(width, height);
    const shortestEdge = Math.min(width, height);

    if (
      longestEdge < UgcCreatePageComponent.MIN_PHOTO_LONGEST_EDGE
      || shortestEdge < UgcCreatePageComponent.MIN_PHOTO_SHORTEST_EDGE
    ) {
      return this.translateService.instant('ugc_create.camera.photo_too_small', {
        minWidth: UgcCreatePageComponent.MIN_PHOTO_LONGEST_EDGE,
        minHeight: UgcCreatePageComponent.MIN_PHOTO_SHORTEST_EDGE
      });
    }

    return null;
  }

  private buildPreviewDataUrl(photo: UgcPhotoUploadDTO): string {
    return `data:${photo.contentType || 'image/jpeg'};base64,${photo.base64data}`;
  }

  private toDraftLocationSnapshot(position: GeolocationPosition | null): UgcCreateDraftLocationSnapshot | null {
    if (!position?.coords) {
      return null;
    }

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy ?? null,
      timestamp: position.timestamp ?? Date.now()
    };
  }

  private fromDraftLocationSnapshot(snapshot: UgcCreateDraftLocationSnapshot): GeolocationPosition {
    return {
      coords: {
        latitude: snapshot.latitude,
        longitude: snapshot.longitude,
        accuracy: snapshot.accuracy ?? 0,
        altitude: null,
        altitudeAccuracy: null,
        heading: null,
        speed: null
      },
      timestamp: snapshot.timestamp
    } as GeolocationPosition;
  }

  private handleSubmitSuccess(submission: UgcSubmissionDTO): void {
    this.ugcCreateDraftService.clearDraft(this.territoryCode());
    this.submittedSubmission.set(submission);
    this.flowStep.set('success');
    this.submitState.set('success');
    this.stopCameraStream();
    this.toastService.showSuccessToastWithMessage(this.translateService.instant('ugc_create.submit.success_toast'));
  }

  private handleSubmitError(error: unknown): void {
    this.submitState.set('error');
    this.submissionError.set(this.resolveHttpError(error, 'ugc_create.submit.error'));
    this.toastService.showErrorToast(this.submissionError() ?? this.translateService.instant('ugc_create.submit.error'));
  }

  private resolveHttpError(error: unknown, fallbackKey: string): string {
    const responseError = (error as { error?: unknown })?.error;
    if (typeof responseError === 'string' && responseError.trim().length > 0) {
      return responseError;
    }

    return this.translateService.instant(fallbackKey);
  }
}
